import React from 'react';
import { cn } from '@/lib/core/utils';
import { Button } from '@/components/ui/button';
import { Delete } from 'lucide-react';

interface NumericKeypadProps {
  onDigit: (digit: string) => void;
  onBackspace: () => void;
  onClear: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Touch-friendly numeric keypad for PIN entry
 */
export function NumericKeypad({
  onDigit,
  onBackspace,
  onClear,
  disabled = false,
  className,
}: NumericKeypadProps) {
  const handleKeyPress = (key: string) => {
    if (disabled) return;

    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }

    onDigit(key);
  };

  return (
    <div className={cn('inline-flex flex-col gap-3', className)}>
      {/* Row 1: 1-2-3 */}
      <div className="flex gap-3 justify-center">
        {['1', '2', '3'].map((digit) => (
          <Button
            key={digit}
            type="button"
            variant="outline"
            onClick={() => handleKeyPress(digit)}
            disabled={disabled}
            className={cn(
              'w-20 h-20 text-3xl font-semibold',
              'hover:bg-gray-100 dark:hover:bg-muted',
              'active:scale-95 transition-transform',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {digit}
          </Button>
        ))}
      </div>

      {/* Row 2: 4-5-6 */}
      <div className="flex gap-3 justify-center">
        {['4', '5', '6'].map((digit) => (
          <Button
            key={digit}
            type="button"
            variant="outline"
            onClick={() => handleKeyPress(digit)}
            disabled={disabled}
            className={cn(
              'w-20 h-20 text-3xl font-semibold',
              'hover:bg-gray-100 dark:hover:bg-muted',
              'active:scale-95 transition-transform',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {digit}
          </Button>
        ))}
      </div>

      {/* Row 3: 7-8-9 */}
      <div className="flex gap-3 justify-center">
        {['7', '8', '9'].map((digit) => (
          <Button
            key={digit}
            type="button"
            variant="outline"
            onClick={() => handleKeyPress(digit)}
            disabled={disabled}
            className={cn(
              'w-20 h-20 text-3xl font-semibold',
              'hover:bg-gray-100 dark:hover:bg-muted',
              'active:scale-95 transition-transform',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {digit}
          </Button>
        ))}
      </div>

      {/* Row 4: Clear-0-Backspace */}
      <div className="flex gap-3 justify-center">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            if (!disabled) {
              if ('vibrate' in navigator) navigator.vibrate(50);
              onClear();
            }
          }}
          disabled={disabled}
          className={cn(
            'w-20 h-20 text-base font-medium',
            'text-gray-600 dark:text-gray-400',
            'hover:bg-gray-100 dark:hover:bg-muted',
            'active:scale-95 transition-transform',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          Clear
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => handleKeyPress('0')}
          disabled={disabled}
          className={cn(
            'w-20 h-20 text-3xl font-semibold',
            'hover:bg-gray-100 dark:hover:bg-muted',
            'active:scale-95 transition-transform',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          0
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            if (!disabled) {
              if ('vibrate' in navigator) navigator.vibrate(50);
              onBackspace();
            }
          }}
          disabled={disabled}
          className={cn(
            'w-20 h-20',
            'text-gray-600 dark:text-gray-400',
            'hover:bg-gray-100 dark:hover:bg-muted',
            'active:scale-95 transition-transform',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Delete className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
}
